import type { Screen, ScreenId } from "./ScreenManager";
import { iconCheck, iconFace, iconRobot } from "./assets";

export type PlayerKind = "nobody" | "human" | "computer";
export interface PlayerSlot {
  color: string;
  kind: PlayerKind;
  name: string;
}

export interface PlayerSelection {
  count: 2 | 3 | 4;
  vsComputer: boolean;
  slots: PlayerSlot[];
}

const COLORS = ["var(--p-green)", "var(--p-red)", "var(--p-yellow)", "var(--p-blue)"] as const;

/** Tap-cycle order in computer mode: nobody → human → computer → nobody. */
const KIND_CYCLE: Record<PlayerKind, PlayerKind> = {
  nobody: "human",
  human: "computer",
  computer: "nobody",
};

const LOCAL_CYCLE: Record<PlayerKind, PlayerKind> = {
  nobody: "human",
  human: "nobody",
  computer: "nobody",
};

export class PlayerSelectScreen implements Screen {
  readonly id: ScreenId = "select";

  private count: 2 | 3 | 4 = 2;
  private slots: PlayerSlot[] = [];

  private segButtons: HTMLButtonElement[] = [];
  private slotsEl!: HTMLElement;
  private hintEl!: HTMLElement;
  private playBtn!: HTMLButtonElement;

  constructor(
    private onBack: () => void,
    private onPlay: (selection: PlayerSelection) => void,
    private vsComputer = false,
  ) {
    // Default: 2P → 2 humans, 3P → 3 humans, 4P → 4 humans; tap to make Nobody.
    // Computer mode: Human + Computer for 2P (classic 1v1), rest as above.
    this.slots = COLORS.map((color, i) => {
      let kind: PlayerKind = "nobody";
      if (i < this.count) {
        if (this.vsComputer && i === 1) kind = "computer";
        else kind = "human";
      }
      return { color, kind, name: "" };
    });
  }

  private segPeople(n: 2 | 3 | 4): string {
    const cols = ["#d9364c", "#2f6fd6", "#27a84f", "#eab41f"];
    const people = cols.slice(0, n).map((c) => `<span class="seg__person" style="background:${c}"><svg viewBox="0 0 24 24" fill="white"><circle cx="12" cy="8" r="4.5"/><path d="M5 20a7 7 0 0114 0"/></svg></span>`).join("");
    return `<span class="seg__people">${people}</span>`;
  }
  render(): HTMLElement {
    const el = document.createElement("div");
    el.innerHTML = `
      <section class="panel select-panel" aria-label="Select players">
        <div class="select-panel__header">
          <div class="select-panel__dice" aria-hidden="true">
            <span class="dice-line"></span>
            <span class="dice-cube">🎲</span>
            <span class="dice-line"></span>
          </div>
          <h2><span class="sel-1">Select</span> <span class="sel-2">Players</span></h2>
        </div>
        <div class="seg" role="radiogroup" aria-label="Player count">
          ${([2, 3, 4] as const)
            .map(
              (n) => `
            <button class="seg__btn" data-count="${n}" role="radio">
              ${this.segPeople(n)}
              <span class="seg__label">${n}P</span>
              <span class="seg__check" aria-hidden="true">${iconCheck}</span>
            </button>`,
            )
            .join("")}
        </div>
        <p class="select-hint"></p>
        <div class="slots"></div>
        <div class="select-actions">
          <button class="select-back js-back" aria-label="Back">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8"
                 stroke-linecap="round" stroke-linejoin="round"><polyline points="15 6 9 12 15 18"/></svg>
          </button>
          <button class="select-play js-play"><span>PLAY</span><span class="select-play__arrow" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 6 15 12 9 18"/></svg></span></button>
        </div>
      </section>
    `;

    this.segButtons = [...el.querySelectorAll<HTMLButtonElement>(".seg__btn")];
    this.segButtons.forEach((btn) =>
      btn.addEventListener("click", () => {
        const newCount = Number(btn.dataset.count) as 2 | 3 | 4;
        // Auto-fill newly included seats as Human (so 2P→2 humans, 3P→3, 4P→4).
        if (newCount > this.count) {
          for (let i = this.count; i < newCount; i++) {
            if (this.slots[i].kind === "nobody") {
              this.slots[i].kind = "human";
            }
          }
        }
        this.count = newCount;
        this.sync();
      }),
    );

    this.slotsEl = el.querySelector(".slots")!;
    this.hintEl = el.querySelector(".select-hint")!;
    this.playBtn = el.querySelector(".js-play")!;
    el.querySelector(".js-back")!.addEventListener("click", this.onBack);
    this.playBtn.addEventListener("click", () => {
      if (!this.playBtn.disabled) {
        this.onPlay({ count: this.count, vsComputer: this.vsComputer, slots: this.slots });
      }
    });

    this.sync();
    return el;
  }

  /** Seats taking part in the match (humans + computers). */
  private activeSeats(): number {
    return this.slots.slice(0, this.count).filter((s) => s.kind !== "nobody").length;
  }

  private renderSlots(): void {
    this.slotsEl.innerHTML = "";
    this.slots.slice(0, this.count).forEach((slot, i) => {
      const btn = document.createElement("button");
      btn.className = `slot${slot.kind === "nobody" ? " slot--empty" : ""}`;
      btn.style.setProperty("--slot", slot.color);
      btn.setAttribute("aria-label", `Seat ${i + 1}: ${slot.kind}`);
      const pawnColor = slot.color;
      const pawnIcon = `<span class="slot__pawn" style="color:${pawnColor}"><svg viewBox="0 0 24 28" fill="currentColor"><ellipse cx="12" cy="26" rx="8" ry="2.5" fill="rgba(0,0,0,0.18)"/><path d="M12 2a5 5 0 00-5 5c0 2 1 3.5 2.2 4.5L8 16a3 3 0 003 3h2a3 3 0 003-3l-1.2-4.5A5 5 0 0012 2z"/><circle cx="12" cy="7" r="4.2" fill="white" opacity="0.35"/></svg></span>`;
      let inner = "";
      if (slot.kind === "human") {
        inner = `
          <span class="slot__avatar-wrap"><span class="slot__avatar">${iconFace}</span><span class="slot__crown">👑</span></span>
          <span class="slot__pill"><input class="slot__name" maxlength="12" placeholder="Human ${i + 1}" value="${slot.name.replace(/"/g, "&quot;")}" aria-label="Human ${i + 1} name"></span>
          ${pawnIcon}<span class="slot__spark slot__spark--l">✦</span><span class="slot__spark slot__spark--r">✦</span>`;
      } else if (slot.kind === "computer") {
        inner = `
          <span class="slot__avatar-wrap"><span class="slot__avatar">${iconRobot}</span><span class="slot__crown">👑</span></span>
          <span class="slot__pill"><span class="slot__label">Computer</span></span>
          ${pawnIcon}<span class="slot__spark slot__spark--l">✦</span><span class="slot__spark slot__spark--r">✦</span>`;
      } else {
        inner = `<span class="slot__pill"><span class="slot__label">Nobody</span></span>`;
      }
      btn.innerHTML = inner;

      const input = btn.querySelector<HTMLInputElement>(".slot__name");
      input?.addEventListener("pointerdown", (e) => e.stopPropagation());
      input?.addEventListener("input", () => {
        slot.name = input.value;
      });
      // Tapping inside the name box must NOT flip the seat.
      input?.addEventListener("click", (e) => e.stopPropagation());

      btn.addEventListener("click", () => {
        slot.kind = (this.vsComputer ? KIND_CYCLE : LOCAL_CYCLE)[slot.kind];
        if (slot.kind !== "human") slot.name = "";
        this.renderSlots();
        this.sync();
      });
      this.slotsEl.appendChild(btn);
    });
  }

  private sync(): void {
    this.segButtons.forEach((btn) =>
      btn.classList.toggle("is-selected", Number(btn.dataset.count) === this.count),
    );
    // Seats pushed out by a lower count drop back to Nobody.
    this.slots.forEach((slot, i) => {
      if (i >= this.count && slot.kind !== "nobody") {
        slot.kind = "nobody";
        slot.name = "";
      }
    });
    this.hintEl.textContent = this.vsComputer
      ? "Tap a seat: Human · Computer · Nobody"
      : "Tap a seat to add or remove a player";
    this.renderSlots();
    this.playBtn.disabled = this.activeSeats() < 2;
  }
}
